import { createRouter } from "@tanstack/react-router";
import * as Sentry from "@sentry/tanstackstart-react";
import { PostHogProvider } from "posthog-js/react";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: Number.POSITIVE_INFINITY,
			gcTime: Number.POSITIVE_INFINITY,
			refetchOnWindowFocus: false,
			retry: (failureCount, error) => {
				if (
					typeof error === "object" &&
					error !== null &&
					"code" in error &&
					[404, 401].includes(error.code as unknown as number)
				) {
					return false;
				}

				if (failureCount < 3) {
					return true;
				}

				return false;
			},
		},
	},
});

// Create a new router instance
export const getRouter = () => {
	const router = createRouter({
		routeTree,
		scrollRestoration: true,
		defaultPreloadStaleTime: 0,
		Wrap: ({ children }) => (
			<QueryClientProvider client={queryClient}>
				<PostHogProvider
					apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
					options={{
						api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
						ui_host: import.meta.env.VITE_PUBLIC_POSTHOG_UI_HOST,
						defaults: "2025-05-24",
						capture_exceptions: true, // This enables capturing exceptions using Error Tracking, set to false if you don't want this
						// debug: import.meta.env.MODE === "development",
						disable_session_recording:
							import.meta.env.VITE_PUBLIC_APP_ENV !== "production",
						cookieless_mode: "on_reject",
					}}
				>
					{children}
				</PostHogProvider>
			</QueryClientProvider>
		),
	});

	if (!router.isServer) {
		Sentry.init({
			dsn: import.meta.env.VITE_SEN,
			sendDefaultPii: true,
			orgId: import.meta.env.VITE_SENTRY_ORG_ID,
			integrations: [],
		});
	}

	return router;
};
